'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { cn } from '../../../lib/cn'
import { Markdown } from '../../../lib/markdown'

export interface StatsProps {
  /** Optional heading displayed above stats */
  heading?: string
  /** Optional description displayed below heading */
  description?: string
  stats: {
    value: string
    label: string
  }[]
  className?: string
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
}

const dividerVariants = {
  hidden: { opacity: 0, scaleY: 0 },
  visible: {
    opacity: 1,
    scaleY: 1,
    transition: { duration: 0.4, ease: 'easeOut' as const },
  },
}

const horizontalDividerVariants = {
  hidden: { opacity: 0, scaleX: 0 },
  visible: {
    opacity: 1,
    scaleX: 1,
    transition: { duration: 0.4, ease: 'easeOut' as const },
  },
}

export function Stats({ heading, description, stats, className }: StatsProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const prefersReducedMotion = useReducedMotion()

  if (!stats || stats.length < 3 || stats.length > 4) {
    return null
  }

  const hasHeader = heading || description

  return (
    <div ref={ref} className={cn(hasHeader ? 'py-16 md:py-24 lg:py-32' : 'py-12 md:py-16', className)}>
      <div className="container max-w-7xl mx-auto">
        {/* Optional header */}
        {hasHeader && (
          <motion.div
            className="mx-auto mb-12 max-w-3xl text-center"
            variants={prefersReducedMotion ? undefined : itemVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
          >
            {heading && (
              <h2 className="mb-4 text-3xl font-semibold tracking-tight lg:text-4xl">
                {heading}
              </h2>
            )}
            {description && (
              <Markdown size="lg">{description}</Markdown>
            )}
          </motion.div>
        )}
        <motion.div
          className="mx-auto flex max-w-4xl flex-col items-center justify-center gap-12 md:flex-row md:gap-32"
          variants={prefersReducedMotion ? undefined : containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              className="relative flex flex-col items-center md:flex-row"
              variants={prefersReducedMotion ? undefined : itemVariants}
            >
              {/* Horizontal divider on mobile */}
              {i > 0 && (
                <motion.div
                  className="absolute -top-6 left-1/2 h-px w-16 -translate-x-1/2 bg-border md:hidden"
                  variants={prefersReducedMotion ? undefined : horizontalDividerVariants}
                />
              )}
              {/* Vertical divider on desktop - centered in gap-32 (half = 16) */}
              {i > 0 && (
                <motion.div
                  className="absolute -left-16 hidden h-8 w-px bg-border md:block"
                  variants={prefersReducedMotion ? undefined : dividerVariants}
                />
              )}
              <div className="text-center">
                <div className="whitespace-nowrap text-4xl font-medium">{stat.value}</div>
                <div className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  )
}
